import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { UserCheck, UserX, Clock, Mail, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PendingUsers = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/pending`);
      setPendingUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (userId, action) => {
    try {
      await axios.put(`${API_URL}/users/${userId}?action=${action}`);
      toast.success(action === 'approve' ? 'Usuario aprobado' : 'Usuario rechazado');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Error al procesar la solicitud');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900">Usuarios Pendientes</h1>
          <p className="font-inter text-slate-600 mt-1">Validación de nuevos registros de alumnos</p>
        </div>

        {/* Pending Users List */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          {pendingUsers.length === 0 ? (
            <div className="p-12 text-center">
              <UserCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No hay usuarios pendientes</h3>
              <p className="text-slate-500 mt-1">Todos los registros han sido procesados</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pendingUsers.map((user) => (
                <div key={user.id} className="p-6 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {user.nombre} {user.apellidos}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {user.email}
                        </div>
                        {user.dni && (
                          <span>DNI: {user.dni}</span>
                        )}
                        {user.telefono && (
                          <span>Tel: {user.telefono}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-yellow-100 text-yellow-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Pendiente
                    </Badge>
                    <Button
                      onClick={() => handleValidate(user.id, 'approve')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Aprobar
                    </Button>
                    <Button
                      onClick={() => handleValidate(user.id, 'reject')}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <UserX className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PendingUsers;
